import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { IconButton, Stack } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcPopUp } from '@/assets/images';
import { excludeValues, sortStrings } from '@/components/DataGrid/utils';
import { DATE_FORMAT } from '@/config';

const recordNumber = (): Partial<GridColDef> => ({
  field: 'recordNumber',
  renderCell: (params) => params.row.recordNumber || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  sortComparator: (a, b) => sortStrings(a, b),
});

const recordType = (): Partial<GridColDef> => ({
  field: 'recordType',
  renderCell: (params) => {
    const rawType = String(params.row.recordType || '').toUpperCase();
    if (rawType === 'CASE') return 'Case';
    if (rawType === 'CFS') return 'CFS';
    if (rawType === 'TASK') return 'Task';
    if (rawType === 'WARRANT') return 'Warrant';
    return rawType || '-';
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  sortComparator: (a, b) => sortStrings(a, b),
});

const address = (): Partial<GridColDef> => ({
  field: 'address',
  renderCell: (params) => params.row.address || '-',
});

const dateCreated = (): Partial<GridColDef> => ({
  field: 'createdAt',
  renderCell: (params) =>
    params.row.createdAt
      ? moment(params.row.createdAt).format(DATE_FORMAT)
      : '-',
  filterOperators: getGridDateOperators(true),
  valueGetter: (_value, row) =>
    row?.createdAt ? new Date(row.createdAt) : null,
  sortComparator: (a, b) => {
    const aTime = a instanceof Date ? a.getTime() : 0;
    const bTime = b instanceof Date ? b.getTime() : 0;
    return aTime - bTime;
  },
});

const getRecordPath = (recordTypeValue: string, recordId: string) => {
  const normalizedType = recordTypeValue ? recordTypeValue.toUpperCase() : '';
  switch (normalizedType) {
    case 'CASE':
      return `/cases/${recordId}/overview`;
    case 'CFS':
      return `/cfs/${recordId}`;
    case 'TASK':
      return `/tasks/${recordId}/view`;
    case 'WARRANT':
      return `/tools/wanted-person/view/${recordId}`;
    default:
      return '';
  }
};

const actions = (onUnlink?: (row: any) => void): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params) => {
    const recordId = params.row.recordId || params.row._id;
    const recordTypeValue = params.row.recordType || '';
    const path = recordId ? getRecordPath(recordTypeValue, recordId) : '';

    return (
      <Stack direction="row" spacing={0.5}>
        <IconButton
          onClick={() => {
            if (!path) return;
            window.open(path, '_blank', 'noopener');
          }}
        >
          <IcPopUp className="text-gray-400" />
        </IconButton>
        {onUnlink ? (
          <IconButton onClick={() => onUnlink(params.row)}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    );
  },
});

export const cfsRelatedRecordsCols = (onUnlink?: (row: any) => void) => {
  return {
    recordNumber,
    recordType,
    address,
    createdAt: dateCreated,
    actions: () => actions(onUnlink),
  };
};
